/**
 * nativeShell.js — runtime support for the Capacitor-wrapped store app.
 *
 * The store build wraps the SAME web bundle (webDir 'dist') in a native
 * shell (capacitor.config.ts, appId com.synerflo.droppin). Inside that
 * shell the page origin is capacitor://localhost (iOS) / https://localhost
 * (Android), which breaks three web assumptions this module repairs
 * (mirrors InSpec v2.1130.0 src/utils/nativeShell.js):
 *
 *   1. AUTH — the HttpOnly SameSite=Lax `insite_sid` cookie never rides
 *      from a capacitor origin, so the shell authenticates with the SAME
 *      InSite-ID session JWT carried as `Authorization: Bearer` instead
 *      (issued by /api/auth-login, -signup and -reset-password when the
 *      client asks with `tokenSession: true`; accepted server-side at the
 *      same seam as the cookie — api/shared/insiteId.js
 *      sessionTokenFromReq). The token is a real server-validated session
 *      (every request re-validates against InSite-ID /auth/me), NOT a
 *      parallel auth system. The suite's silent SSO (shared
 *      .insitebg.com.au cookie) is structurally impossible in a wrapper,
 *      so the wrapped app always shows the real login screen — expected.
 *   2. API HOST — every fetch in the app is a relative '/api/...' path.
 *      The shell bundles the front end, so those must target the live API
 *      host. A boot-time fetch wrapper rewrites them. CapacitorHttp
 *      (capacitor.config.ts) routes them through the native http stack,
 *      so no CORS surface is needed on the server.
 *   3. NAVIGATION — window.open/_blank has no tab UI in a WebView; external
 *      links route through the system browser (@capacitor/browser).
 *
 * ON THE WEB EVERY EXPORT IS A NO-OP OR PASS-THROUGH: isNativeShell() is
 * false (no window.Capacitor), initNativeShell() returns immediately, and
 * openExternal falls back to plain window.open. Nothing here imports any
 * @capacitor package — plugins are reached via the window.Capacitor global
 * the native runtime injects — so the web bundle carries zero new bytes of
 * native tooling and web behaviour is bit-for-bit unchanged.
 */

// The live production origin the wrapped app talks to. The wrapper always
// bundles a production build (docs/CAPACITOR.md), so this is a constant,
// not an env switch — a dev override can come later via VITE_API_BASE.
export const NATIVE_API_BASE = 'https://droppin.insitebg.com.au';

// Storage key for the session token (Capacitor Preferences, with a
// localStorage fallback for dev/simulated runs).
const TOKEN_KEY = 'droppin_session_token';

/* ── native detection ────────────────────────────────────────────── */

export function isNativeShell() {
  try {
    return !!(
      typeof window !== 'undefined' &&
      window.Capacitor &&
      typeof window.Capacitor.isNativePlatform === 'function' &&
      window.Capacitor.isNativePlatform()
    );
  } catch (e) { return false; }
}

function capPlugin(name) {
  try {
    return (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins[name]) || null;
  } catch (e) { return null; }
}

/* ── session-token store (Preferences → localStorage fallback) ───── */

let tokenCache = null;      // string | null
let tokenLoaded = null;     // Promise resolved once the stored token is read

async function readStoredToken() {
  const prefs = capPlugin('Preferences');
  if (prefs && typeof prefs.get === 'function') {
    try {
      const r = await prefs.get({ key: TOKEN_KEY });
      if (r && r.value) return r.value;
    } catch (e) { /* fall through to localStorage */ }
  }
  try { return localStorage.getItem(TOKEN_KEY) || null; } catch (e) { return null; }
}

/** Resolves the cached session token (loads it from storage once). */
export function getNativeToken() {
  if (!tokenLoaded) {
    tokenLoaded = readStoredToken().then((t) => { tokenCache = t || null; return tokenCache; });
  }
  return tokenLoaded.then(() => tokenCache);
}

export async function setNativeToken(token) {
  tokenCache = token || null;
  tokenLoaded = Promise.resolve(tokenCache);
  const prefs = capPlugin('Preferences');
  if (prefs && typeof prefs.set === 'function') {
    try { await prefs.set({ key: TOKEN_KEY, value: String(token || '') }); return; }
    catch (e) { /* fall through */ }
  }
  try { localStorage.setItem(TOKEN_KEY, String(token || '')); } catch (e) { /* best-effort */ }
}

export async function clearNativeToken() {
  tokenCache = null;
  tokenLoaded = Promise.resolve(null);
  const prefs = capPlugin('Preferences');
  if (prefs && typeof prefs.remove === 'function') {
    try { await prefs.remove({ key: TOKEN_KEY }); } catch (e) { /* best-effort */ }
  }
  try { localStorage.removeItem(TOKEN_KEY); } catch (e) { /* best-effort */ }
}

/* ── fetch rewrite (native only — installed by initNativeShell) ──── */

// True when this URL is an app-relative API call that must be re-pointed at
// the live host. Only the local shell origin is rewritten — absolute URLs
// (blob SAS uploads, external services) pass through untouched.
function needsRewrite(u) {
  return u.origin === window.location.origin && (u.pathname === '/api' || u.pathname.startsWith('/api/'));
}

function installFetchRewrite() {
  const origFetch = window.fetch.bind(window);
  window.fetch = async function nativeShellFetch(input, init) {
    try {
      const isRequest = typeof Request !== 'undefined' && input instanceof Request;
      const rawUrl = isRequest ? input.url : String(input);
      const u = new URL(rawUrl, window.location.href);
      if (needsRewrite(u)) {
        const target = NATIVE_API_BASE + u.pathname + u.search;
        const token = await getNativeToken();
        if (isRequest) {
          input = new Request(target, input);
          if (token && !input.headers.get('Authorization')) {
            input.headers.set('Authorization', 'Bearer ' + token);
          }
        } else {
          input = target;
          init = Object.assign({}, init);
          const headers = new Headers((init && init.headers) || undefined);
          if (token && !headers.get('Authorization')) {
            headers.set('Authorization', 'Bearer ' + token);
          }
          init.headers = headers;
        }
      }
    } catch (e) { /* on any rewrite hiccup, send the request unmodified */ }
    return origFetch(input, init);
  };
}

/* ── external links / window.open ────────────────────────────────── */

// Resolve an app-relative href for use OUTSIDE the shell (system browser):
// a '/r/<token>' share link must point at the live host, not
// capacitor://localhost.
function externalUrl(url) {
  const s = String(url || '');
  if (isNativeShell() && s.startsWith('/')) return NATIVE_API_BASE + s;
  return s;
}

/**
 * Open a URL "in a new tab". Web: window.open exactly as before. Native:
 * http(s) URLs open in the system browser via @capacitor/browser (falling
 * back to window.open, which Capacitor bridges); blob:/data: URLs fall back
 * to window.open.
 */
export function openExternal(url) {
  if (!url) return;
  if (!isNativeShell()) {
    try { window.open(url, '_blank', 'noopener'); } catch (e) { /* popup blocked */ }
    return;
  }
  const abs = externalUrl(url);
  if (/^https?:/i.test(abs)) {
    const browser = capPlugin('Browser');
    if (browser && typeof browser.open === 'function') {
      browser.open({ url: abs }).catch(() => { try { window.open(abs, '_blank'); } catch (e) {} });
      return;
    }
  }
  try { window.open(abs, '_blank'); } catch (e) { /* WebView refused */ }
}

/**
 * onClick companion for <a target="_blank"> anchors. On the web it does
 * nothing (the anchor keeps its default new-tab behaviour). In the native
 * shell it cancels the default (WKWebView has no tabs) and routes the href
 * through openExternal.
 */
export function interceptExternalClick(e) {
  if (!isNativeShell()) return;
  const a = e && e.currentTarget;
  const href = a && (a.getAttribute('href') || a.href);
  if (!href) return;
  e.preventDefault();
  openExternal(href);
}

/* ── boot ────────────────────────────────────────────────────────── */

/**
 * Called from main.jsx BEFORE React mounts. No-op on the web. Natively:
 *   - starts the stored-token load;
 *   - installs the '/api' → live-host fetch rewrite with the
 *     Authorization: Bearer header.
 * (No entry-route rewrite needed — DropPin's wrapper boots '/', which is
 * the right surface; the auth gate in AppShell shows the login screen.)
 */
export function initNativeShell() {
  if (!isNativeShell()) return;
  getNativeToken();          // warm the token cache
  installFetchRewrite();
}
